// EMR Sync — two responsibilities (D3/D4):
//  1. Failed sync: surface emr_sync_status='failed' with a retry action.
//  2. Reconciliation drift: surface reconciliation_events (Healthie-originated changes CC did not
//     author) with an alert + a resolve action that stamps resolved_at. Healthie direct scheduling is
//     an unsupported workflow (per docs/RUNBOOK.md); these are drift signals, not an authoritative read.
import { useState } from "react";
import { Btn, Surface } from "@championcare/ui";
import { useApi } from "../api";
import { useAppAuth } from "../auth";
import { useAsync } from "../lib/useAsync";
import { formatDateTime, shortId } from "../lib/format";
import { resolveDrift, type ResolveAction } from "../lib/reconcileWrites";
import { Loading, ErrorState, EmptyState, PageHeader, StatusBadge } from "../components/ui";

export function EmrSync() {
  const api = useApi();
  const { getToken } = useAppAuth();
  const failed = useAsync(() => api.emrSync(), []);
  const drift = useAsync(() => api.reconciliation(), []);
  const [busy, setBusy] = useState<string | null>(null); // row id (retry) or `${id}:${action}` (resolve)
  const [resolveErr, setResolveErr] = useState<string | null>(null);

  async function retry(id: string) {
    setBusy(id);
    try {
      await api.retrySync(id);
      failed.reload();
    } finally {
      setBusy(null);
    }
  }
  // True-resolve: the operator chooses the repair direction — adopt Healthie's state, or
  // re-push CC's state to Healthie — not a single "acknowledge" button.
  async function resolve(id: string, action: ResolveAction) {
    setBusy(`${id}:${action}`);
    setResolveErr(null);
    try {
      await resolveDrift(getToken, id, action);
      drift.reload();
    } catch (e) {
      setResolveErr(e instanceof Error ? e.message : "Resolve failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page">
      <PageHeader title="EMR Sync" sub="Failed syncs + reconciliation drift." />

      <h2 className="section-title">Failed syncs</h2>
      <Surface className="card">
        {failed.loading ? (
          <Loading />
        ) : failed.error ? (
          <ErrorState message={failed.error} />
        ) : (failed.data ?? []).length ? (
          <table className="tbl">
            <thead>
              <tr>
                <th>Visit</th>
                <th>EMR sync</th>
                <th>Zoom sync</th>
                <th>Updated</th>
                <th className="tbl__actions">Action</th>
              </tr>
            </thead>
            <tbody>
              {(failed.data ?? []).map((r) => (
                <tr key={r.id}>
                  <td className="mono">{shortId(r.id)}</td>
                  <td>
                    <StatusBadge value={r.emr_sync_status} />
                  </td>
                  <td>
                    <StatusBadge value={r.zoom_sync_status} />
                  </td>
                  <td className="mono">{formatDateTime(r.updated_at)}</td>
                  <td className="tbl__actions">
                    <Btn
                      as="button"
                      variant="primary"
                      disabled={busy !== null || r.emr_sync_status !== "failed"}
                      onClick={() => retry(r.id)}
                    >
                      {busy === r.id ? "…" : "Retry"}
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState>No failed syncs. 🎉</EmptyState>
        )}
      </Surface>

      <h2 className="section-title">Reconciliation drift</h2>
      {resolveErr && <div className="state state--error">{resolveErr}</div>}
      <Surface className="card">
        {drift.loading ? (
          <Loading />
        ) : drift.error ? (
          <ErrorState message={drift.error} />
        ) : (drift.data ?? []).length ? (
          <table className="tbl">
            <thead>
              <tr>
                <th>Source</th>
                <th>Kind</th>
                <th>Healthie appt</th>
                <th>Detected</th>
                <th className="tbl__actions">Action</th>
              </tr>
            </thead>
            <tbody>
              {(drift.data ?? []).map((d) => (
                <tr key={d.id} className="drift-row">
                  <td>{d.source}</td>
                  <td>
                    <span className="tag amber">{d.kind.replace(/_/g, " ")}</span>
                  </td>
                  <td className="mono">{shortId(d.healthie_appointment_id)}</td>
                  <td className="mono">{formatDateTime(d.created_at)}</td>
                  <td className="tbl__actions">
                    <span className="row-actions">
                      <Btn
                        as="button"
                        variant="ghost"
                        disabled={busy !== null}
                        title="Adopt Healthie's state and clear the drift"
                        onClick={() => resolve(d.id, "accept_healthie")}
                      >
                        {busy === `${d.id}:accept_healthie` ? "…" : "Adopt Healthie"}
                      </Btn>
                      <Btn
                        as="button"
                        variant="primary"
                        disabled={busy !== null}
                        title="Re-mirror Champion Care's state to Healthie"
                        onClick={() => resolve(d.id, "push_cc")}
                      >
                        {busy === `${d.id}:push_cc` ? "…" : "Push CC"}
                      </Btn>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState>No open drift events.</EmptyState>
        )}
      </Surface>
    </div>
  );
}
